import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, FileText, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReports } from '../hooks/useReports';
import { useAppSelector } from '../../../../stores';
import { Report } from '../../../lib/types';
import { FamlaServices } from '../../../../services';

interface ReportsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onReportSelect: (report: Report) => void;
    mapId: string;
    accessToken: string;
    isAnalyseSaved: boolean
}

export const ReportsModal = ({ isOpen, onClose, onReportSelect, mapId, isAnalyseSaved }: ReportsModalProps) => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const language = localStorage.getItem('currentLanguage') === "fr" ? "fr-FR" : "en-EN"
    const [hoveredReport, setHoveredReport] = useState<string | null>(null);

    const [report, setReport] = useState<Report[]>([])

    const loadAllAnalysies = useCallback(async () => {
        try {
            let data = await FamlaServices("api_host", `/report/saved/map/${mapId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                const DataReport = data.body.data;
                setReport(DataReport);
            }
        } catch (error) {
        }
    }, [mapId, users.access_token]);


    useEffect(() => {
        if (mapId && isOpen) {
            loadAllAnalysies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapId, isAnalyseSaved, isOpen, loadAllAnalysies]); // Recharger à chaque ouverture du modal




    const filteredReports = report.filter((report: Report) =>
        report.name?.toLowerCase().includes(searchTerm?.toLowerCase())
    );


    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(language, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden animate-scaleIn">
                <div className="border-b border-gray-100 p-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">{t('features.swimlane.analysis')}</h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X size={20} className="text-gray-600" />
                    </button>
                </div>

                <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('words.search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-2">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        </div>
                    ) : filteredReports.length > 0 ? (
                        <ul className="space-y-2">
                            {filteredReports.map((rep: Report) => (
                                <li
                                    key={rep._id}
                                    className="relative border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => onReportSelect(rep)}
                                    onMouseEnter={() => setHoveredReport(rep._id)}
                                    onMouseLeave={() => setHoveredReport(null)}
                                >
                                    <div className="p-3">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-2">
                                                <FileText size={18} className="text-blue-500 flex-shrink-0" />
                                                <h3 className="font-medium text-gray-900">{rep.name}</h3>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-500">
                                                <Calendar size={14} className="mr-1" />
                                                {formatDate(rep.created_at)}
                                            </div>
                                        </div>

                                        {/* {hoveredReport === rep._id && (
                                            <div className="mt-2 text-sm text-gray-600 line-clamp-2 bg-gray-50 p-2 rounded border border-gray-100">
                                                <p className="font-medium">Prompt: </p>
                                                <p className="text-gray-500">{rep.prompt}</p>
                                            </div>
                                        )} */}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                            <FileText size={48} className="text-gray-300 mb-3" />
                            <p className="text-gray-500">
                                {searchTerm
                                    ? t('features.swimlane.noReportsMatchSearch')
                                    : t('features.swimlane.noSavedReports')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};